import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, Kbd, Skeleton, type DropdownMenuItem } from '@/components/ui';
import { HierarchyRow, type HierarchyRowDragHandlers } from './HierarchyRow';
import { HierarchySearchResults } from './HierarchySearchResults';
import type { HierarchyPageVM } from './useHierarchyPage';
import type { OrganizationTreeNode } from './types';

export interface HierarchyTreePanelProps {
    vm: HierarchyPageVM;
}

function InsertionLine({ label }: { label: string }) {
    return (
        <span className="hz-row-wrap hz-insertion-wrap" aria-hidden="true">
            <span className="hz-insertion-line">
                <span className="hz-insertion-dot" />
                <span className="hz-insertion-track" />
                <span className="hz-insertion-chip">{label}</span>
            </span>
        </span>
    );
}

/** Frame 33's "ĐANG TẢI" state — indented skeleton rows, not a bare spinner. */
function TreeSkeleton() {
    const { t } = useTranslation();
    return (
        <div className="hz-tree-skeleton" role="status" aria-live="polite">
            <span className="portal-sr-only">
                {t('console.hierarchy.treePanel.loadingSrLabel')}
            </span>
            {[0, 1, 2, 3].map((i) => (
                <span key={i} className="hz-skeleton-row" data-depth={i % 3}>
                    <Skeleton height="24px" width="24px" />
                    <Skeleton height="0.75rem" width={`${55 - i * 6}%`} />
                </span>
            ))}
        </div>
    );
}

/**
 * EAA-1818: left panel of `/console/hierarchy` — the lazily expanded
 * `Organization.parent` tree and the "LEVEL · TOÀN TỔ CHỨC, KHÔNG THEO ĐƠN VỊ"
 * group underneath it. The search input and "Thu gọn" button that used to open
 * this panel now live in `HierarchyPage`'s toolbar (frame 29); this panel only
 * reads `vm.searchActive` to decide whether `HierarchySearchResults` replaces
 * the tree body.
 *
 * The Level group is separate from the tree BY DESIGN, not by omission: a Level
 * belongs to the organization and is shared across every đơn vị in it, so it is
 * not a node and has no parent to drag it into (see `types.ts`).
 */
export function HierarchyTreePanel({ vm }: HierarchyTreePanelProps) {
    const { t } = useTranslation();
    const {
        canManage,
        canManageTree,
        visibleNodes,
        expandedIds,
        toggleExpanded,
        isLoadingRoot,
        isErrorRoot,
        rootOrgQuery,
        rootTruncated,
        visibleLevels,
        levelBreakdown,
        searchActive,
        selectedOrgId,
        setSelectedOrgId,
        dragSource,
        dragTarget,
        illegal,
        dropChipLabel,
        renamingId,
        renameDraft,
        setRenameDraft,
        renameError,
        beginRename,
        cancelRename,
        commitRename,
        renameOrg,
        openCreateChild,
        openMoveDialog,
        openAdoptDialog,
        setConfirmDeactivateOrgId,
        reactivateOrg,
        copyOrgCode,
        openRetype,
        unitTypesById,
        setConfirmDeactivateLevel,
        onRowDragStart,
        onRowDragOver,
        onRowDrop,
        onRowDragEnd,
        onTreeKeyDown,
        inlineDropError,
        setInlineDropError,
        moveError,
        dismissMoveError,
        pendingMove,
        movePending,
        completedMove,
        dismissCompletedMove,
        undoCompletedMove,
        undoPending,
        announcement,
    } = vm;

    const dragHandlers: HierarchyRowDragHandlers = useMemo(
        () => ({
            onDragStart: onRowDragStart,
            onDragOver: onRowDragOver,
            onDrop: onRowDrop,
            onDragEnd: onRowDragEnd,
            onKeyDown: onTreeKeyDown,
        }),
        [onRowDragStart, onRowDragOver, onRowDrop, onRowDragEnd, onTreeKeyDown]
    );

    /**
     * Frame 30's context menu. "Đổi loại đơn vị" replaced "Đặt cấp bậc (tier)"
     * in EAA-1838 — same slot, and it can no longer CLEAR the value, because a
     * đơn vị always has a type (see `OrgUnitType`).
     *
     * It is gated on `canManage`, NOT `canManageTree`, and that difference is the
     * server's rather than a guess: `OrganizationDetailView.patch` applies its
     * super-admin gate to `is_active` and `parent` only, so retyping is an
     * org-admin-permitted act exactly like rename. Authoring the VOCABULARY is the
     * super-admin act, and that now lives inline in the create modal's picker.
     */
    function orgActions(node: OrganizationTreeNode): DropdownMenuItem[] {
        const items: DropdownMenuItem[] = [];
        if (canManageTree) {
            items.push({
                key: 'add-child',
                label: t('console.hierarchy.treePanel.actionAddChild'),
                onSelect: () => openCreateChild(node),
            });
        }
        if (canManage) {
            items.push({
                key: 'rename',
                label: t('console.hierarchy.treePanel.actionRename'),
                onSelect: () => beginRename(node),
            });
        }
        if (canManageTree && node.depth > 0) {
            items.push({
                key: 'move',
                label: t('console.hierarchy.treePanel.actionMoveTo'),
                onSelect: () => openMoveDialog(node),
            });
        }
        /**
         * EAA-1891: cross-tree adoption. Offered at EVERY depth, including the
         * root row (`depth === 0`), unlike "Chuyển đến…" above — a root org is
         * precisely the case this action exists for, and it is the one node the
         * in-tree move cannot express (there is no parent inside this tree to
         * re-target it to).
         */
        if (canManageTree) {
            items.push({
                key: 'adopt',
                label: t('admin.organizations.move.action'),
                onSelect: () => openAdoptDialog(node),
            });
        }
        items.push({
            key: 'copy-code',
            label: t('console.hierarchy.treePanel.actionCopyCode'),
            onSelect: () => copyOrgCode(node.organization.code),
        });
        if (canManage) {
            items.push({
                key: 'retype',
                label: t('console.hierarchy.treePanel.actionRetype'),
                onSelect: () => openRetype(node),
            });
        }
        if (canManageTree && node.depth > 0) {
            if (node.organization.is_active) {
                items.push({
                    key: 'deactivate',
                    label: t('console.hierarchy.treePanel.actionDeactivate'),
                    danger: true,
                    onSelect: () => setConfirmDeactivateOrgId(node.id),
                });
            } else {
                items.push({
                    key: 'reactivate',
                    label: t('console.hierarchy.treePanel.actionReactivate'),
                    onSelect: () => reactivateOrg.mutate(node.id),
                });
            }
        }
        return items;
    }

    function levelActions(levelId: string, isActive: boolean): DropdownMenuItem[] {
        if (!canManage || !isActive) return [];
        const level = vm.levels.find((l) => l.id === levelId);
        if (!level) return [];
        return [
            {
                key: 'deactivate',
                label: t('console.hierarchy.treePanel.actionStopLevel'),
                danger: true,
                onSelect: () => setConfirmDeactivateLevel(level),
            },
        ];
    }

    /** The đơn vị whose re-parent PATCH is currently in flight, if any. */
    const movingOrgId = movePending ? (pendingMove?.node.id ?? null) : null;

    const targetIs = (kind: 'org' | 'level', id: string, mode: 'before' | 'onto' | 'after') =>
        Boolean(dragSource) &&
        dragTarget?.kind === kind &&
        dragTarget.id === id &&
        dragTarget.mode === mode;

    // Illegal styling belongs to the whole ROW being hovered, independent of
    // before/onto/after — a Level dragged over an đơn vị is illegal no matter
    // which third of that row's height the pointer sits in.
    const illegalForRow = (kind: 'org' | 'level', id: string) =>
        dragTarget?.kind === kind && dragTarget.id === id ? illegal : null;

    let treeBody: React.ReactNode;
    if (searchActive) {
        treeBody = <HierarchySearchResults vm={vm} />;
    } else if (isLoadingRoot) {
        treeBody = <TreeSkeleton />;
    } else if (isErrorRoot) {
        treeBody = (
            <ErrorState
                description={t('console.hierarchy.treePanel.loadErrorDescription')}
                onRetry={() => void rootOrgQuery.refetch()}
                retrying={rootOrgQuery.isFetching}
            />
        );
    } else if (visibleNodes.length === 0) {
        treeBody = (
            <EmptyState
                title={t('console.hierarchy.treePanel.emptyTitle')}
                description={t('console.hierarchy.treePanel.emptyDescription')}
            />
        );
    } else {
        treeBody = (
            <>
                {visibleNodes.map((node, i) => {
                    const next = visibleNodes[i + 1];
                    // EAA-1838: the badge resolves the org's `unit_type` id against
                    // the root's loaded vocabulary. An id with no matching row means
                    // the vocabulary has not arrived yet (or the type belongs to
                    // another root) — no chip is drawn rather than a raw uuid.
                    const unitType = node.organization.unit_type
                        ? unitTypesById.get(node.organization.unit_type)
                        : undefined;
                    const showInsertionAfter =
                        targetIs('org', node.id, 'after') ||
                        (next ? targetIs('org', next.id, 'before') : false);
                    return (
                        <React.Fragment key={node.id}>
                            {i === 0 && targetIs('org', node.id, 'before') && (
                                <InsertionLine label={dropChipLabel} />
                            )}
                            <HierarchyRow
                                row={{
                                    id: node.id,
                                    kind: 'org',
                                    depth: node.depth,
                                    label: node.organization.name,
                                    code: node.organization.code,
                                }}
                                state={{
                                    // Frame 28 shows both pills. "đang chuyển"
                                    // wins while the PATCH is in flight, since
                                    // that is the more time-sensitive fact.
                                    badge:
                                        movingOrgId === node.id
                                            ? t('console.hierarchy.treePanel.badgeMoving')
                                            : node.organization.is_active
                                              ? undefined
                                              : t('console.hierarchy.treePanel.badgeInactive'),
                                    unitTypeLabel: unitType?.name,
                                    unitTypeHidden: unitType ? !unitType.is_active : undefined,
                                    statusDot: node.organization.is_active ? 'on' : 'off',
                                    muted: !node.organization.is_active,
                                    selected: selectedOrgId === node.id,
                                    moving: dragSource?.kind === 'org' && dragSource.id === node.id,
                                    ontoActive: targetIs('org', node.id, 'onto'),
                                    illegal: illegalForRow('org', node.id),
                                }}
                                expand={{
                                    // Not "has children" — "not known to be a
                                    // leaf". Before the first fetch a node's
                                    // children are unknown, so the twisty must
                                    // still be offered (see types.ts).
                                    expandable: !node.childrenLoaded || node.children.length > 0,
                                    // Single source of truth, root included —
                                    // `useOrgTree` seeds the root into
                                    // `expandedIds` precisely so its twisty is
                                    // real rather than permanently "open".
                                    expanded: expandedIds.has(node.id),
                                    loading: node.childrenLoading,
                                    onToggle: () => toggleExpanded(node.id),
                                }}
                                rename={{
                                    active: renamingId === node.id,
                                    draft: renameDraft,
                                    error: renameError,
                                    pending: renameOrg.isPending,
                                    onDraftChange: setRenameDraft,
                                    onCommit: commitRename,
                                    onCancel: cancelRename,
                                }}
                                actions={orgActions(node)}
                                draggable={canManageTree && node.depth > 0}
                                onSelect={() => setSelectedOrgId(node.id)}
                                onRenameShortcut={canManage ? () => beginRename(node) : undefined}
                                onMoveShortcut={
                                    canManageTree && node.depth > 0
                                        ? () => openMoveDialog(node)
                                        : undefined
                                }
                                dragHandlers={dragHandlers}
                            />
                            {showInsertionAfter && <InsertionLine label={dropChipLabel} />}
                        </React.Fragment>
                    );
                })}
                {rootTruncated && (
                    <p className="hz-card-note">
                        {t('console.hierarchy.treePanel.truncatedNote')}
                    </p>
                )}
            </>
        );
    }

    return (
        <div className="hz-shell">
            {/* EAA-1818: the search input and "Thu gọn" button moved up into
                `HierarchyPage`'s toolbar (frame 29 puts them next to the page title,
                not inside this panel). `searchQuery`/`setSearchQuery`/`collapseAll`
                now live only in the page shell — this panel still reads
                `searchActive` off the vm to decide whether `HierarchySearchResults`
                replaces the tree body below. */}

            {/* EAA-1818: the đơn vị tree and the Level list are TWO `role="tree"`
                regions inside one scroll container, not one. They are separate
                hierarchies (a Level is not a node of the org tree), and the
                search-result view that replaces the first region renders plain
                buttons — nesting those under a `role="tree"` would be invalid
                ARIA and would announce non-nodes as tree items. */}
            <div className="hz-tree">
                <div
                    className="hz-tree-group"
                    role={searchActive ? undefined : 'tree'}
                    aria-label={
                        searchActive ? undefined : t('console.hierarchy.treePanel.treeAriaLabel')
                    }
                >
                    {treeBody}
                </div>

                <div className="hz-level-group-header">
                    <span className="hz-level-group-label">
                        {t('console.hierarchy.treePanel.levelGroupLabel')}
                    </span>
                    <span className="hz-level-divider" />
                </div>

                {visibleLevels.length === 0 && (
                    <EmptyState title={t('console.hierarchy.treePanel.noLevelsTitle')} />
                )}

                <div
                    className="hz-tree-group"
                    role="tree"
                    aria-label={t('console.hierarchy.treePanel.levelsAriaLabel')}
                >
                    {visibleLevels.map((level, i) => (
                        <React.Fragment key={level.id}>
                            {targetIs('level', level.id, 'before') && (
                                <InsertionLine
                                    label={t('console.hierarchy.treePanel.dropIntoLevelLabel')}
                                />
                            )}
                            <HierarchyRow
                                row={{
                                    id: level.id,
                                    kind: 'level',
                                    depth: 0,
                                    label: level.name,
                                    code: level.role_key,
                                }}
                                state={{
                                    countLabel: String(levelBreakdown.byLevelId[level.id] ?? 0),
                                    badge: level.is_active
                                        ? undefined
                                        : t('console.hierarchy.treePanel.badgeLevelPaused'),
                                    muted: !level.is_active,
                                    moving:
                                        dragSource?.kind === 'level' && dragSource.id === level.id,
                                    illegal: illegalForRow('level', level.id),
                                }}
                                actions={levelActions(level.id, level.is_active)}
                                dragHandlers={dragHandlers}
                            />
                            {i === visibleLevels.length - 1 &&
                                targetIs('level', level.id, 'after') && (
                                    <InsertionLine
                                        label={t('console.hierarchy.treePanel.dropIntoLevelLabel')}
                                    />
                                )}
                        </React.Fragment>
                    ))}
                </div>

                {levelBreakdown.noLevel > 0 && (
                    <span className="hz-row-wrap">
                        <span className="hz-row hz-row--warn-static">
                            <span className="hz-name">
                                {t('console.hierarchy.treePanel.noLevelRowLabel')}
                            </span>
                            <span className="hz-count">{levelBreakdown.noLevel}</span>
                        </span>
                    </span>
                )}
            </div>

            <HierarchyTreeFooter
                inlineDropError={inlineDropError ?? moveError}
                onDismissError={() => {
                    setInlineDropError(null);
                    dismissMoveError();
                }}
                completedMove={completedMove}
                onUndo={undoCompletedMove}
                onDismissCompleted={dismissCompletedMove}
                undoPending={undoPending}
            />

            <span className="portal-sr-only" role="status" aria-live="assertive">
                {announcement}
            </span>
        </div>
    );
}

interface HierarchyTreeFooterProps {
    inlineDropError: string | null;
    onDismissError: () => void;
    completedMove: { orgName: string; targetName: string } | null;
    onUndo: () => void;
    onDismissCompleted: () => void;
    undoPending: boolean;
}

/**
 * EAA-1818: the footer is one of three things — a failure, frame 29's success
 * banner, or the keyboard hint.
 *
 * The success copy deliberately does NOT repeat the mock's "N tài khoản đổi phạm
 * vi ứng dụng". Application scope lives per organization and is not inherited, so
 * a parent change grants and revokes nothing; claiming otherwise would be the one
 * thing on this screen that is untrue.
 */
function HierarchyTreeFooter({
    inlineDropError,
    onDismissError,
    completedMove,
    onUndo,
    onDismissCompleted,
    undoPending,
}: HierarchyTreeFooterProps) {
    const { t } = useTranslation();

    if (inlineDropError) {
        return (
            <div className="hz-footer">
                <span className="hz-footer-error" role="alert">
                    {inlineDropError}
                    <button
                        type="button"
                        className="hz-footer-error-dismiss"
                        onClick={onDismissError}
                    >
                        {t('common.close')}
                    </button>
                </span>
            </div>
        );
    }

    if (completedMove) {
        return (
            <div className="hz-footer hz-footer--success" role="status">
                <span className="hz-footer-success-body">
                    <span className="hz-footer-success-title">
                        {t('console.hierarchy.treePanel.moveSuccessTitle', {
                            name: completedMove.orgName,
                        })}
                    </span>
                    <span className="hz-footer-success-note">
                        {t('console.hierarchy.treePanel.moveSuccessNote', {
                            target: completedMove.targetName,
                        })}
                    </span>
                </span>
                <button
                    type="button"
                    className="hz-footer-undo"
                    onClick={onUndo}
                    disabled={undoPending}
                >
                    {undoPending
                        ? t('console.hierarchy.treePanel.undoingLabel')
                        : t('console.hierarchy.treePanel.undoButton')}
                </button>
                <button
                    type="button"
                    className="hz-footer-error-dismiss"
                    onClick={onDismissCompleted}
                >
                    {t('common.close')}
                </button>
            </div>
        );
    }

    return (
        <div className="hz-footer">
            <span className="hz-footer-hint">
                {/* EAA-1845: the hint interleaves plain text with <Kbd> chips, so it is
                    translated via <Trans> with named self-closing tags rather than a
                    plain t() call — a template-string interpolation would have to
                    serialize the <Kbd> elements to strings, losing their markup. */}
                <Trans
                    i18nKey="console.hierarchy.treePanel.keyboardHint"
                    components={{
                        space: <Kbd>Space</Kbd>,
                        enter: <Kbd>Enter</Kbd>,
                        esc: <Kbd>Esc</Kbd>,
                        f2: <Kbd>F2</Kbd>,
                        ctrl: <Kbd>Ctrl</Kbd>,
                        kbdm: <Kbd>M</Kbd>,
                    }}
                />
            </span>
        </div>
    );
}

export default HierarchyTreePanel;
